import type { AppAction } from 'actions/types/ActionTypes'
import { UPDATE_LOYALTY_ESIM_CODE } from 'actions/actionConstants'
import requestLoyaltyEsimPromoCode from 'api/loyalty/requestLoyaltyEsimPromoCode'
import delay from 'lib/utils/delay'

const RETRY_DELAY_MS = 15_000
const MAX_RETRIES = 16
const GENERATING_THRESHOLD_MS = 2_000

/**
 * Retrieves an order's promo code, including the period where lux-loyalty is generating it.
 * The caller should add the order's display data to Redux; this action only advances
 * its code phase and supplies the generated code when it becomes available.
 */
function pollEsimPromoCode(
  order: {
    orderId: string
    customerId: string
    tier: App.LoyaltyTier | undefined
    tripEnded: boolean
    availableDate: string | undefined
  },
  options?: { deferGeneratingCopy?: boolean },
): AppAction {
  return async(dispatch, getState) => {
    let generatingTimerId: ReturnType<typeof setTimeout> | undefined

    if (options?.deferGeneratingCopy) {
      generatingTimerId = setTimeout(() => {
        const currentState = getState()
        if (currentState.loyalty.esimPromoCodes.codesByOrderId[order.orderId]?.phase === 'loading') {
          dispatch({
            type: UPDATE_LOYALTY_ESIM_CODE,
            orderId: order.orderId,
            update: { phase: 'generating' },
          })
        }
      }, GENERATING_THRESHOLD_MS)
    }

    try {
      // Make one request immediately, then wait between at most 16 further attempts.
      for (let retryCount = 0; retryCount <= MAX_RETRIES; retryCount++) {
        const response = await requestLoyaltyEsimPromoCode(order.orderId, order.customerId)

        if (getState().loyalty.esimPromoCodes.codesByOrderId[order.orderId]?.phase === 'success') return

        switch (response.status) {
          case 'success':
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: {
                phase: 'success',
                code: response.result.promocode ?? undefined,
                tier: response.result.tier ?? order.tier,
              },
            })
            return

          case 'generating':
          case 'not_found':
            // A past trip can only reveal an already-generated code; do not keep trying to create one.
            if (order.tripEnded) {
              dispatch({
                type: UPDATE_LOYALTY_ESIM_CODE,
                orderId: order.orderId,
                update: { phase: 'not_found' },
              })
              return
            }

            // The service is either generating the code or has not created it yet. Keep the card in its pending state.
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'generating' },
            })

            if (retryCount < MAX_RETRIES) {
              await delay(RETRY_DELAY_MS)
              // Re-check after waiting for the same reason as the guard above.
              if (getState().loyalty.esimPromoCodes.codesByOrderId[order.orderId]?.phase === 'success') return
              continue
            }

            // Terminal: Code generation did not settle within the polling window.
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'error' },
            })
            return

          case 'not_ready':
            // Outside the redemption window, a past trip has no remaining path to receive a code.
            if (order.tripEnded) {
              dispatch({
                type: UPDATE_LOYALTY_ESIM_CODE,
                orderId: order.orderId,
                update: { phase: 'not_found' },
              })
              return
            }
            // For an upcoming trip, surface when the customer can try again instead of treating this as an error.
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: {
                phase: 'available_later',
                availableDate: order.availableDate,
              },
            })
            return

          default:
            // An unexpected API or transport failure cannot be recovered by this polling loop.
            dispatch({
              type: UPDATE_LOYALTY_ESIM_CODE,
              orderId: order.orderId,
              update: { phase: 'error' },
            })
            return
        }
      }
    } finally {
      if (generatingTimerId !== undefined) clearTimeout(generatingTimerId)
    }
  }
}

export default pollEsimPromoCode
